import { useCallback, useContext, useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { useAPI, useUpdatePageTitle, createTitleFromSegments } from "@humansignal/core";
import { memberColumns as extensionMemberColumns } from "@afriannotate/extensions";
import { useAuth } from "@humansignal/core/providers/AuthProvider";
import { Button, Checkbox, Tooltip, useToast } from "@humansignal/ui";
import { IconCross, IconPencil, IconTrash } from "@humansignal/icons";
import { IconPauseThin, IconPlayThin } from "../../components/RowIcons/RowIcons";
import { ProjectContext } from "../../providers/ProjectProvider";
import { cn } from "../../utils/bem";
import { askConfirm } from "../../utils/ask";
import { Pagination, usePage, usePageSize } from "../../components/Pagination/Pagination";
import { SettingsPageHeader } from "../../components/SettingsPageHeader/SettingsPageHeader";
import { Loading } from "../../components/Loading/Loading";
import { ProjectNav } from "../ProjectDashboard/ProjectNav";
import { useTranslation } from "../../i18n/LocaleProvider";
import "./MembersSettings.prefix.css";

const block = cn("project-members");

const ROLE_OPTIONS = [
  { value: "", label: "Inherit org role" },
  { value: "OW", label: "Owner" },
  { value: "AD", label: "Admin" },
  { value: "MA", label: "Manager" },
  { value: "RE", label: "Reviewer" },
  { value: "AN", label: "Annotator" },
  { value: "GU", label: "Guest" },
];

const ROLE_LABELS = Object.fromEntries(ROLE_OPTIONS.filter((r) => r.value).map((r) => [r.value, r.label]));

const roleLabel = (value) => (value ? (ROLE_LABELS[value] ?? value) : "Inherits org");

/**
 * Contributors tab (crowdsource slice 6). Non-org people who joined a
 * public+open project via Discover — ``ProjectContributor`` rows, a
 * distinct class from ``ProjectMember`` / org members shown on the main
 * tab. Managers can prune a row (revokes access; existing annotations
 * are kept). Read-only for non-managers. Backend gates both the list
 * and the delete to managers regardless of what the UI shows.
 */
const ContributorsTab = ({ projectId, canManage }) => {
  const { t } = useTranslation();
  const api = useAPI();
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  // Consent-gated reputation lines for PENDING requesters — fetched
  // per user; only opted-in profiles come back (raw fetch idiom: the
  // endpoint 403s/hides for everyone else and the chip just doesn't
  // render).
  const [repMap, setRepMap] = useState({});

  const load = useCallback(async () => {
    if (!projectId) return;
    const res = await api.callApi("projectContributors", {
      params: { pk: projectId },
      suppressError: true,
    });
    if (res && !res.error) setRows(Array.isArray(res.results) ? res.results : []);
    setLoaded(true);
  }, [api, projectId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!canManage) return undefined;
    const ids = rows.filter((c) => c.status === "pending").map((c) => c.user_id);
    const missing = ids.filter((id) => repMap[id] === undefined);
    if (missing.length === 0) return undefined;
    let alive = true;
    (async () => {
      const fetched = {};
      await Promise.all(
        missing.map(async (id) => {
          try {
            const r = await fetch(`/api/users/${id}/reputation/`, {
              credentials: "same-origin",
              cache: "no-store",
            });
            fetched[id] = r.ok ? await r.json() : null;
          } catch {
            fetched[id] = null;
          }
        }),
      );
      if (alive) setRepMap((prev) => ({ ...prev, ...fetched }));
    })();
    return () => {
      alive = false;
    };
  }, [canManage, rows, repMap]);

  // "1,240 validated units, 96% acceptance, 4 orgs" — only what the
  // requester consented to share, only rates above the volume floor.
  const repLine = (body) => {
    const p = body?.opted_in ? body.profile : null;
    if (!p) return null;
    const parts = [`${Math.round(p.total_units).toLocaleString()} validated units`];
    if (p.reviews?.acceptance_rate != null) parts.push(`${Math.round(p.reviews.acceptance_rate * 100)}% acceptance`);
    if (p.evaluations?.pass_rate != null) parts.push(`${Math.round(p.evaluations.pass_rate * 100)}% eval pass`);
    parts.push(`${p.organizations} org${p.organizations === 1 ? "" : "s"}`);
    return parts.join(", ");
  };

  const onRemove = async (userId) => {
    if (
      !(await askConfirm({
        title: "Remove this contributor?",
        body: "They lose access to this project. Annotations they already submitted are kept.",
        okText: "Remove",
        danger: true,
      }))
    ) {
      return;
    }
    setRemovingId(userId);
    const res = await api.callApi("projectContributorRemove", {
      params: { pk: projectId, userPk: userId },
      suppressError: true,
    });
    setRemovingId(null);
    if (!res || res.error) {
      toast.show({ message: res?.error || "Couldn't remove that contributor.", type: "error" });
      return;
    }
    setRows((prev) => prev.filter((c) => c.user_id !== userId));
    toast.show({ message: "Contributor removed." });
  };

  const onApprove = async (userId) => {
    setRemovingId(userId);
    const res = await api.callApi("projectContributorApprove", {
      params: { pk: projectId, userPk: userId },
      suppressError: true,
    });
    setRemovingId(null);
    if (!res || res.error) {
      toast.show({ message: res?.error || "Couldn't approve that request.", type: "error" });
      return;
    }
    setRows((prev) => prev.map((c) => (c.user_id === userId ? { ...c, status: "active" } : c)));
    toast.show({ message: t("pages.settings.members.contributor_approved", undefined, "Contributor approved.") });
  };

  // Phase F — appoint or demote a facilitator. Same manager-gated
  // endpoint as approval; the body flag drives the role change.
  const onToggleFacilitator = async (userId, want) => {
    const res = await api.callApi("projectContributorApprove", {
      params: { pk: projectId, userPk: userId },
      body: { is_facilitator: want },
      suppressError: true,
    });
    if (!res || res.error) {
      toast.show({ message: res?.error || "Couldn't change the facilitator role.", type: "error" });
      return;
    }
    setRows((prev) => prev.map((c) => (c.user_id === userId ? { ...c, is_facilitator: want } : c)));
    toast.show({
      message: want
        ? t(
            "pages.settings.members.facilitator_appointed",
            undefined,
            "Facilitator appointed. Their invite link is now active.",
          )
        : t(
            "pages.settings.members.facilitator_demoted",
            undefined,
            "Facilitator demoted. Their invite link was deactivated.",
          ),
    });
  };

  const onDecline = async (userId) => {
    if (
      !(await askConfirm({
        title: t("pages.settings.members.decline_title", undefined, "Decline this request?"),
        body: t(
          "pages.settings.members.decline_body",
          undefined,
          "The request is removed. They can request to join again later.",
        ),
        okText: t("pages.settings.members.decline", undefined, "Decline"),
        danger: true,
      }))
    ) {
      return;
    }
    setRemovingId(userId);
    const res = await api.callApi("projectContributorRemove", {
      params: { pk: projectId, userPk: userId },
      suppressError: true,
    });
    setRemovingId(null);
    if (!res || res.error) {
      toast.show({ message: res?.error || "Couldn't decline that request.", type: "error" });
      return;
    }
    setRows((prev) => prev.filter((c) => c.user_id !== userId));
    toast.show({ message: t("pages.settings.members.request_declined", undefined, "Request declined.") });
  };

  if (!loaded) return <Loading />;

  if (rows.length === 0) {
    return (
      <div
        className={block.elem("empty").toClassName()}
        style={{ padding: "24px 0", color: "var(--color-neutral-content-subtler)" }}
      >
        No one has joined as a contributor yet. When this project is public and open, people who join it from Discover
        appear here.
      </div>
    );
  }

  const pending = rows.filter((c) => c.status === "pending");
  const active = rows.filter((c) => c.status !== "pending");

  // Same table markup + classes as the members table, minus the
  // role / role-source / quality / bulk-select columns (contributors
  // have no project role — they're a flat non-member list).
  const renderRow = (c, actions, dateLabel) => {
    const name = [c.first_name, c.last_name].filter(Boolean).join(" ") || "No name set";
    let when = "";
    try {
      when = new Date(c.joined_at).toLocaleDateString();
    } catch {
      when = "";
    }
    // Consent-gated credibility line for a pending request — links to
    // the requester's full reputation pane. Absent unless they opted in.
    const rep = c.status === "pending" ? repLine(repMap[c.user_id]) : null;
    return (
      <div key={c.user_id} className={block.elem("row").toClassName()}>
        <div className={block.elem("col").mod({ member: true }).toClassName()}>
          <Avatar user={{ initials: (name || c.email || "?").slice(0, 1).toUpperCase() }} />
          <span className={block.elem("name").toClassName()}>
            {name}
            {rep && (
              <Link
                to={`/users/${c.user_id}/analytics/reputation`}
                title={t(
                  "pages.settings.members.reputation_link_title",
                  undefined,
                  "Shared reputation profile, open full track record",
                )}
                style={{
                  display: "block",
                  fontSize: "var(--font-size-caption)",
                  fontWeight: 500,
                  color: "var(--color-neutral-content-subtler, #64748b)",
                  textDecoration: "none",
                }}
              >
                {rep}
              </Link>
            )}
          </span>
          {c.is_facilitator && (
            <span
              title={t(
                "pages.settings.members.facilitator_badge_title",
                undefined,
                "Facilitator, recruits contributors via an attributed invite link",
              )}
              style={{
                fontSize: "var(--font-size-caption)",
                fontWeight: 700,
                padding: "1px 8px",
                borderRadius: 999,
                marginLeft: 8,
                background: "var(--color-positive-emphasis-subtle)",
                color: "var(--color-positive-content)",
              }}
            >
              {t("pages.settings.members.facilitator", undefined, "Facilitator")}
            </span>
          )}
        </div>
        <div className={block.elem("col").mod({ email: true }).toClassName()}>{c.email}</div>
        <div className={block.elem("col").mod({ managed: true }).toClassName()} title={dateLabel}>
          {when}
        </div>
        {canManage && <div className={block.elem("col").mod({ actions: true }).toClassName()}>{actions(c)}</div>}
      </div>
    );
  };

  const head = (dateCol) => (
    <div className={block.elem("thead").toClassName()}>
      <div className={block.elem("col").mod({ member: true }).toClassName()}>Name</div>
      <div className={block.elem("col").mod({ email: true }).toClassName()}>Email</div>
      <div className={block.elem("col").mod({ managed: true }).toClassName()}>{dateCol}</div>
      {canManage && <div className={block.elem("col").mod({ actions: true }).toClassName()}>Actions</div>}
    </div>
  );

  return (
    <div className={block.elem("table-wrap").toClassName()}>
      {canManage && pending.length > 0 && (
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: "var(--font-size-body-small)", fontWeight: 600, margin: "0 0 8px" }}>
            {t(
              "pages.settings.members.pending_requests",
              { count: pending.length },
              `Pending requests (${pending.length})`,
            )}
          </div>
          <div className={block.elem("table").toClassName()}>
            {head("Requested")}
            {pending.map((c) =>
              renderRow(c, (row) => (
                <div style={{ display: "flex", gap: 8 }}>
                  <Button
                    variant="primary"
                    look="filled"
                    size="small"
                    onClick={() => onApprove(row.user_id)}
                    disabled={removingId === row.user_id}
                    waiting={removingId === row.user_id}
                  >
                    {t("pages.settings.members.approve", undefined, "Approve")}
                  </Button>
                  <Button
                    variant="negative"
                    look="outlined"
                    size="small"
                    onClick={() => onDecline(row.user_id)}
                    disabled={removingId === row.user_id}
                  >
                    {t("pages.settings.members.decline", undefined, "Decline")}
                  </Button>
                </div>
              )),
            )}
          </div>
        </div>
      )}

      {active.length > 0 && (
        <div className={block.elem("table").toClassName()}>
          {head("Joined")}
          {active.map((c) =>
            renderRow(c, (row) => (
              <div style={{ display: "flex", gap: 8 }}>
                <Button
                  variant="neutral"
                  look="outlined"
                  size="small"
                  onClick={() => onToggleFacilitator(row.user_id, !row.is_facilitator)}
                  disabled={removingId === row.user_id}
                >
                  {row.is_facilitator
                    ? t("pages.settings.members.demote_facilitator", undefined, "Demote")
                    : t("pages.settings.members.make_facilitator", undefined, "Make facilitator")}
                </Button>
                <Button
                  variant="negative"
                  look="outlined"
                  size="small"
                  onClick={() => onRemove(row.user_id)}
                  disabled={removingId === row.user_id}
                  waiting={removingId === row.user_id}
                >
                  Remove
                </Button>
              </div>
            )),
          )}
        </div>
      )}
    </div>
  );
};

const Avatar = ({ user }) => (
  <span className={block.elem("avatar").toClassName()}>
    {user.avatar ? (
      <img src={user.avatar} alt="" className={block.elem("avatar-img").toClassName()} />
    ) : (
      user.initials || "?"
    )}
  </span>
);

/** Everything about one member in one place: their role, and anything
 *  an extension adds (the tracks they are on, say). Shown only where
 *  there is more than the role to set; otherwise the row itself is
 *  edited. */
const EditMemberModal = ({ projectId, member, sections, roleOptions, onSaved, onClose }) => {
  const api = useAPI();
  const toast = useToast();
  const [role, setRole] = useState(member.role ?? "");
  const [busy, setBusy] = useState(false);
  // Each section hands back a function that saves it.
  const [savers] = useState(() => new Map());
  const name =
    [member.user_first_name, member.user_last_name].filter(Boolean).join(" ") || member.user_email || "This member";

  const save = async () => {
    setBusy(true);
    let trouble = null;
    if (role !== (member.role ?? "")) {
      const res = await api.callApi("projectUpdateMember", {
        params: { pk: projectId, userPk: member.user },
        body: { role },
        errorFilter: () => true,
      });
      if (!res || res.error) trouble = res?.error ?? "The role could not be saved.";
    }
    for (const saver of savers.values()) {
      try {
        await saver?.();
      } catch (e) {
        trouble = trouble ?? String(e?.message ?? e);
      }
    }
    setBusy(false);
    if (trouble) return toast.show({ message: trouble, type: "error" });
    toast.show({ message: `${name} saved.` });
    onSaved?.();
    onClose();
  };

  return (
    <div
      className={block.elem("modal-overlay").toClassName()}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={block.elem("modal").toClassName()}>
        <div className={block.elem("modal-header").toClassName()}>
          <h2 className={block.elem("modal-title").toClassName()}>{name}</h2>
          <button className={block.elem("modal-close").toClassName()} onClick={onClose} aria-label="Close">
            <IconCross />
          </button>
        </div>
        <div className={block.elem("modal-body").toClassName()}>
          <label className={block.elem("edit-row").toClassName()}>
            <span>Role</span>
            <select
              className={block.elem("role-select").toClassName()}
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              {roleOptions.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>
          {sections.map((section) => (
            <div key={section.key} className={block.elem("edit-section").toClassName()}>
              <span className={block.elem("edit-section-label").toClassName()}>
                {section.edit.label ?? section.label}
              </span>
              <section.edit.Component
                projectId={projectId}
                member={member}
                register={(fn) => savers.set(section.key, fn)}
              />
            </div>
          ))}
        </div>
        <div className={block.elem("modal-actions").toClassName()}>
          <Button size="small" look="outlined" variant="negative" onClick={onClose}>
            Cancel
          </Button>
          <Button size="small" waiting={busy} disabled={busy} onClick={save} data-testid="member-edit-save">
            Save
          </Button>
        </div>
      </div>
    </div>
  );
};

const AddMemberModal = ({ projectId, orgRole, sections = [], onClose, onAdded }) => {
  const { t } = useTranslation();
  const api = useAPI();
  const toast = useToast();
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState({});
  const [saving, setSaving] = useState(false);
  const [addSavers] = useState(() => new Map());

  useEffect(() => {
    api.callApi("projectNonMembers", { params: { pk: projectId } }).then((res) => {
      setLoading(false);
      if (res && !res.error) setCandidates(Array.isArray(res) ? res : []);
    });
  }, [api, projectId]);

  const filtered = candidates.filter((u) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      (u.email ?? "").toLowerCase().includes(q) ||
      (u.first_name ?? "").toLowerCase().includes(q) ||
      (u.last_name ?? "").toLowerCase().includes(q)
    );
  });

  const toggle = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));
  const setRole = (id, role) => setSelected((s) => ({ ...s, [`${id}:role`]: role }));

  const submit = async () => {
    const picks = candidates.filter((u) => selected[u.id]);
    if (picks.length === 0) {
      toast.show({ message: t("pages.settings.members_settings.select_at_least_one_user"), type: "error" });
      return;
    }
    setSaving(true);
    for (const u of picks) {
      const role = selected[`${u.id}:role`] ?? "";
      await api.callApi("projectAddMember", {
        params: { pk: projectId },
        body: { user: u.id, role },
      });
    }
    // Whatever else the project has for its people (a track to put
    // them on) is done with the same Add.
    for (const saver of addSavers.values()) {
      try {
        await saver?.(picks.map((u) => u.id));
      } catch (e) {
        toast.show({ message: String(e?.message ?? e), type: "error" });
      }
    }
    setSaving(false);
    toast.show({ message: `Added ${picks.length} member${picks.length === 1 ? "" : "s"}` });
    onAdded?.();
    onClose();
  };

  return (
    <div
      className={block.elem("modal-overlay").toClassName()}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={block.elem("modal").toClassName()}>
        <div className={block.elem("modal-header").toClassName()}>
          <h2 className={block.elem("modal-title").toClassName()}>
            {t("pages.settings.members_settings.add_members_to_project")}
          </h2>
          <button
            className={block.elem("modal-close").toClassName()}
            onClick={onClose}
            aria-label={t("pages.settings.members_settings.close")}
          >
            <IconCross />
          </button>
        </div>
        <div className={block.elem("modal-body").toClassName()}>
          <input
            className={block.elem("search").toClassName()}
            placeholder={t("pages.settings.members_settings.search_by_name_or_email")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {loading ? (
            <Loading minHeight={180} />
          ) : filtered.length === 0 ? (
            <div className={block.elem("empty").toClassName()}>
              No org members available. Add users to the org first.
            </div>
          ) : (
            <ul className={block.elem("candidate-list").toClassName()}>
              {filtered.map((u) => (
                <li key={u.id} className={block.elem("candidate").mod({ checked: !!selected[u.id] }).toClassName()}>
                  <label className={block.elem("candidate-row").toClassName()}>
                    <Checkbox
                      checked={!!selected[u.id]}
                      onChange={() => toggle(u.id)}
                      ariaLabel={`Select ${u.email}`}
                    />
                    <Avatar user={u} />
                    <div className={block.elem("candidate-info").toClassName()}>
                      <span className={block.elem("candidate-name").toClassName()}>
                        {[u.first_name, u.last_name].filter(Boolean).join(" ") || "No name set"}
                      </span>
                      <span className={block.elem("candidate-email").toClassName()}>{u.email}</span>
                    </div>
                  </label>
                  {selected[u.id] && (
                    <select
                      className={block.elem("role-select").toClassName()}
                      value={selected[`${u.id}:role`] ?? ""}
                      onChange={(e) => setRole(u.id, e.target.value)}
                    >
                      {ROLE_OPTIONS.map((r) => (
                        <option key={r.value} value={r.value}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
        {sections.length ? (
          <div className={block.elem("modal-extra").toClassName()}>
            {sections.map((section) =>
              section.add?.Component ? (
                <div key={section.key} className={block.elem("edit-section").toClassName()}>
                  <span className={block.elem("edit-section-label").toClassName()}>
                    {section.add.label ?? section.label}
                  </span>
                  <section.add.Component projectId={projectId} register={(fn) => addSavers.set(section.key, fn)} />
                </div>
              ) : null,
            )}
          </div>
        ) : null}
        <div className={block.elem("modal-actions").toClassName()}>
          <Button type="button" variant="negative" look="outlined" size="small" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" variant="primary" size="small" onClick={submit} disabled={saving} waiting={saving}>
            {saving ? "Adding…" : "Add selected"}
          </Button>
        </div>
      </div>
    </div>
  );
};

// Columns shown in the members table. Kept as a list (rather than a
// boolean-flag object) so the Columns dropdown reads in natural order
// and new columns land in a single place. Each has a ``default``
// flag for the first-load visibility.
const COLUMNS = [
  { key: "name", label: "Name", default: true },
  { key: "email", label: "Email", default: true },
  { key: "role", label: "Role", default: true },
  { key: "quality", label: "Quality", default: false },
  { key: "actions", label: "Actions", default: true },
];

export const MembersSettings = () => {
  const { t } = useTranslation();
  const api = useAPI();
  const history = useHistory();
  const { user } = useAuth();
  const toast = useToast();
  const { project } = useContext(ProjectContext);
  useUpdatePageTitle(createTitleFromSegments([project?.title, "Members Settings"]));
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [savingRole, setSavingRole] = useState(null);
  // A project whose only member setting is the role is edited in the
  // row itself; where an extension adds more (a split into tracks), one
  // dialog holds all of it.
  const [editingRow, setEditingRow] = useState(null);
  const [editing, setEditing] = useState(null);
  // Two-tab split on a PUBLIC project (crowdsource slice 6): org/project
  // "Members" vs non-member "Contributors" who joined via Discover.
  // Private projects have no contributor concept, so the tab strip is
  // hidden and only the members table shows.
  const isPublic = project?.visibility === "public";
  const [activeTab, setActiveTab] = useState("members");

  // ── Filter / view state ────────────────────────────────────────────
  // Kept client-side because ``projectMembers`` returns the full list
  // in one shot. ``hideInherited`` hides members who don't have an
  // explicit project role override (i.e. they only see the project
  // because of their org role).
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [hideInherited, setHideInherited] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [columns, setColumns] = useState(() => new Set(COLUMNS.filter((c) => c.default).map((c) => c.key)));
  // Columns an extension adds, where this project has such a thing
  // (a split into tracks, say), and what each has loaded.
  const extraColumns = (extensionMemberColumns ?? []).filter((c) => {
    try {
      return !c.shows || c.shows(project);
    } catch {
      return false;
    }
  });
  const [extraData, setExtraData] = useState({});
  const [extraRound, setExtraRound] = useState(0);
  const editSections = extraColumns.filter((c) => c.edit?.Component);

  useEffect(() => {
    if (!project?.id || !extraColumns.length) return;
    let alive = true;
    (async () => {
      const loaded = {};
      for (const column of extraColumns) {
        try {
          loaded[column.key] = await column.load?.(project.id);
        } catch {
          loaded[column.key] = null;
        }
      }
      if (alive) setExtraData(loaded);
    })();
    return () => {
      alive = false;
    };
  }, [project?.id, extraColumns.length, extraRound]);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const toggleColumn = (key) =>
    setColumns((s) => {
      const next = new Set(s);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  const hasCol = (key) => columns.has(key);

  const [page, setPage] = usePage("mpage", 1);
  const [pageSize, setPageSize] = usePageSize("mpage_size", 10);

  const projectId = project?.id;
  const [perms, setPerms] = useState(null);

  useEffect(() => {
    if (!projectId) return;
    api.callApi("projectMyPermissions", { params: { pk: projectId } }).then((res) => {
      if (res && !res.error) setPerms(res);
    });
  }, [api, projectId]);

  const canManage = !!perms?.can_manage_members;

  const load = useCallback(() => {
    if (!projectId) return;
    setLoading(true);
    api.callApi("projectMembers", { params: { pk: projectId } }).then((res) => {
      setLoading(false);
      if (res && !res.error) setMembers(Array.isArray(res) ? res : (res.results ?? []));
    });
  }, [api, projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const updateRole = async (member, role) => {
    setSavingRole(member.id);
    const res = await api.callApi("projectUpdateMember", {
      params: { pk: projectId, userPk: member.user },
      body: { role },
    });
    setSavingRole(null);
    if (!res || res.error) {
      toast.show({ message: res?.error ?? "Failed to update role", type: "error" });
    } else {
      toast.show({ message: t("pages.settings.members_settings.role_updated") });
      load();
    }
  };

  // Flip ``enabled`` back to True. The backend clears the
  // ``suspended_by_evaluation`` flag as a side effect so the next
  // evaluate_annotator pass doesn't immediately pause them again on the
  // old below-threshold score.
  const reEnable = async (member) => {
    const res = await api.callApi("projectUpdateMember", {
      params: { pk: projectId, userPk: member.user },
      body: { enabled: true },
    });
    if (res && !res.error) {
      toast.show({ message: t("pages.settings.members_settings.member_reenabled") });
      load();
    } else {
      toast.show({ message: res?.error ?? "Failed to re-enable", type: "error" });
    }
  };

  // Manual pause / resume — same ``enabled`` flag the eval
  // suspension uses, but triggered by a manager from the members
  // page. Backend fires an in-app notification to the affected user
  // either way so they know their access state flipped.
  const pauseMember = async (member) => {
    const ok = await askConfirm({
      title: `Pause ${member.user_email || "this member"}?`,
      body: "They won't be able to open tasks or save annotations on this project until you resume them. Already-submitted work is unaffected.",
      okText: "Pause",
      danger: true,
    });
    if (!ok) return;
    const res = await api.callApi("projectUpdateMember", {
      params: { pk: projectId, userPk: member.user },
      body: { enabled: false },
    });
    if (res && !res.error) {
      toast.show({ message: t("pages.settings.members_settings.member_paused") });
      load();
    } else {
      toast.show({ message: res?.error ?? "Failed to pause", type: "error" });
    }
  };

  const resumeMember = async (member) => {
    const res = await api.callApi("projectUpdateMember", {
      params: { pk: projectId, userPk: member.user },
      body: { enabled: true },
    });
    if (res && !res.error) {
      toast.show({ message: t("pages.settings.members_settings.member_resumed") });
      load();
    } else {
      toast.show({ message: res?.error ?? "Failed to resume", type: "error" });
    }
  };

  const removeMember = async (member) => {
    const ok = await askConfirm({
      title: "Remove project member?",
      body: `${member.user_email} will lose access to this project. Their org membership is unaffected.`,
      okText: "Remove",
      danger: true,
    });
    if (!ok) return;
    const res = await api.callApi("projectRemoveMember", {
      params: { pk: projectId, userPk: member.user },
    });
    if (!res || res.error) {
      toast.show({ message: res?.error ?? "Failed to remove", type: "error" });
    } else {
      toast.show({ message: t("pages.settings.members_settings.removed_from_project") });
      load();
    }
  };

  // ── Derived data (filter + search) ───────────────────────────────
  // Order: search text → role filter → hide-inherited. Each step is
  // a no-op when its input is empty so the default view shows
  // everything. ``isInherited`` treats a blank ``m.role`` as the
  // member inheriting the project's role from their org-level role.
  const filteredMembers = members.filter((m) => {
    const q = query.trim().toLowerCase();
    if (q) {
      const haystack = `${m.user_email ?? ""} ${m.user_first_name ?? ""} ${m.user_last_name ?? ""}`.toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    if (roleFilter && (m.role ?? "") !== roleFilter) return false;
    if (hideInherited && !m.role) return false;
    return true;
  });

  const total = filteredMembers.length;
  const visible = filteredMembers.slice((page - 1) * pageSize, page * pageSize);

  const selectedInView = visible.filter((m) => selected.has(m.id)).length;
  const allVisibleSelected = visible.length > 0 && selectedInView === visible.length;
  const toggleSelect = (id) =>
    setSelected((s) => {
      const next = new Set(s);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  const toggleSelectAll = () =>
    setSelected((s) => {
      if (allVisibleSelected) {
        const next = new Set(s);
        visible.forEach((m) => next.delete(m.id));
        return next;
      }
      const next = new Set(s);
      visible.forEach((m) => next.add(m.id));
      return next;
    });
  const clearSelection = () => setSelected(new Set());

  const removeSelected = async () => {
    const ids = [...selected];
    if (!ids.length) return;
    const ok = await askConfirm({
      title: "Remove selected members?",
      body: `${ids.length} member${ids.length === 1 ? "" : "s"} will lose project access. Org memberships are unaffected.`,
      okText: "Remove",
      danger: true,
    });
    if (!ok) return;
    const picks = members.filter((m) => ids.includes(m.id));
    for (const m of picks) {
      await api.callApi("projectRemoveMember", {
        params: { pk: projectId, userPk: m.user },
      });
    }
    toast.show({ message: `Removed ${picks.length} member${picks.length === 1 ? "" : "s"}` });
    clearSelection();
    load();
  };

  return (
    <div className={`${block.toClassName()} ${cn("settings-page").mod({ wide: true }).toClassName()}`}>
      <SettingsPageHeader
        title={t("pages.settings.members_settings.project_members")}
        subtitle={t("pages.settings.members_settings.manage_this_projects_members_and_their_r")}
      />

      {/* Members vs Contributors tabs — only on a public project. Mirrors
          the ProjectDashboard → Analytics tab-strip look. */}
      {isPublic && (
        <div className={block.elem("tabs").toClassName()} role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "members"}
            className={block
              .elem("tab")
              .mod({ active: activeTab === "members" })
              .toClassName()}
            onClick={() => setActiveTab("members")}
          >
            Members
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === "contributors"}
            className={block
              .elem("tab")
              .mod({ active: activeTab === "contributors" })
              .toClassName()}
            onClick={() => setActiveTab("contributors")}
          >
            Contributors
          </button>
        </div>
      )}

      {isPublic && activeTab === "contributors" && <ContributorsTab projectId={projectId} canManage={canManage} />}

      {(!isPublic || activeTab === "members") && (
        <>
          {/* Bulk-selection banner — only shows when at least one row is
          checked. Mirrors HumanSignal's "All members selected (N total)
          / Clear Selection" pattern so destructive bulk actions read
          as intentional rather than hidden behind row menus. */}
          {selected.size > 0 && (
            <div className={block.elem("bulk-bar").toClassName()} role="status">
              <span className={block.elem("bulk-label").toClassName()}>
                {selected.size === total && total > 0
                  ? `All members selected (${total} total)`
                  : `${selected.size} selected`}
              </span>
              <div className={block.elem("bulk-actions").toClassName()}>
                {canManage && (
                  <Button variant="negative" look="outlined" size="smaller" onClick={removeSelected}>
                    Remove
                  </Button>
                )}
                <Button variant="neutral" look="outlined" size="smaller" onClick={clearSelection}>
                  Clear Selection
                </Button>
              </div>
            </div>
          )}

          {/* Filter toolbar — search + role filter on the left, View
          dropdown + page-size selector hug the right edge so the two
          sets of controls (filter vs layout) sit on opposite ends. */}
          <div className={block.elem("toolbar").toClassName()}>
            <input
              className={block.elem("search").toClassName()}
              placeholder={t("pages.settings.members_settings.search_members")}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(1);
              }}
            />

            <select
              className={block.elem("role-filter").toClassName()}
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setPage(1);
              }}
              aria-label={t("pages.settings.members_settings.filter_by_role")}
            >
              <option value="">{t("pages.settings.members_settings.all_roles")}</option>
              {ROLE_OPTIONS.filter((r) => r.value).map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>

            <div className={block.elem("toolbar-spacer").toClassName()} aria-hidden />

            <select
              className={block.elem("pagesize").toClassName()}
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
              aria-label={t("pages.settings.members_settings.members_per_page")}
            >
              {[5, 10, 25, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>

            <div className={block.elem("columns-wrap").toClassName()}>
              <Button
                variant="neutral"
                look="outlined"
                size="small"
                // The same chevron the app draws on every dropdown.
                data-chevron
                aria-expanded={columnsOpen}
                onClick={() => setColumnsOpen((v) => !v)}
              >
                View
              </Button>
              {columnsOpen && (
                <>
                  <div className={block.elem("columns-backdrop").toClassName()} onClick={() => setColumnsOpen(false)} />
                  <div className={block.elem("columns-menu").toClassName()} role="menu">
                    {/* Filters section — row-level filters that don't
                    touch the column layout. Sits above the column
                    checklist so the two concerns are visually
                    separated inside one dropdown. */}
                    <div className={block.elem("columns-section-label").toClassName()}>Filters</div>
                    <label className={block.elem("columns-item").toClassName()}>
                      <Checkbox
                        checked={hideInherited}
                        onChange={() => {
                          setHideInherited((v) => !v);
                          setPage(1);
                        }}
                        ariaLabel="Hide members whose role is inherited from the organization"
                      />
                      <span>{t("pages.settings.members_settings.hide_inherited")}</span>
                    </label>

                    <div className={block.elem("columns-divider").toClassName()} aria-hidden="true" />

                    <div className={block.elem("columns-section-label").toClassName()}>Columns ({columns.size})</div>
                    {COLUMNS.map((c) => (
                      <label key={c.key} className={block.elem("columns-item").toClassName()}>
                        <Checkbox
                          checked={hasCol(c.key)}
                          onChange={() => toggleColumn(c.key)}
                          ariaLabel={`Toggle ${c.label} column`}
                        />
                        <span>{c.label}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>
            {canManage && (!isPublic || activeTab === "members") ? (
              <Button variant="primary" onClick={() => setAddOpen(true)} data-testid="add-members">
                Add members
              </Button>
            ) : null}
          </div>

          <div className={block.elem("table-wrap").toClassName()}>
            {loading ? (
              <Loading fill />
            ) : total === 0 ? (
              <div className={block.elem("empty").toClassName()}>
                {members.length === 0
                  ? "No explicit project members yet. Org owners and admins always have access."
                  : "No members match the current filters."}
              </div>
            ) : (
              <div className={block.elem("table").toClassName()}>
                <div className={block.elem("thead").toClassName()}>
                  <div className={block.elem("col").mod({ check: true }).toClassName()}>
                    <Checkbox
                      checked={allVisibleSelected}
                      onChange={toggleSelectAll}
                      ariaLabel="Select all visible members"
                    />
                  </div>
                  {hasCol("name") && (
                    <div className={block.elem("col").mod({ member: true }).toClassName()}>
                      {t("pages.settings.members_settings.name")}
                    </div>
                  )}
                  {hasCol("email") && (
                    <div className={block.elem("col").mod({ email: true }).toClassName()}>
                      {t("pages.settings.members_settings.email")}
                    </div>
                  )}
                  {hasCol("role") && (
                    <div className={block.elem("col").mod({ role: true }).toClassName()}>
                      {t("pages.settings.members_settings.role")}
                    </div>
                  )}
                  {hasCol("quality") && (
                    <div className={block.elem("col").mod({ quality: true }).toClassName()}>
                      {t("pages.settings.members_settings.quality")}
                    </div>
                  )}
                  {extraColumns.map((column) => (
                    <div key={column.key} className={block.elem("col").mod({ extra: true }).toClassName()}>
                      {column.label}
                    </div>
                  ))}
                  {hasCol("actions") && (
                    <div className={block.elem("col").mod({ actions: true }).toClassName()}>
                      {t("pages.settings.members_settings.actions")}
                    </div>
                  )}
                </div>
                {visible.map((m) => {
                  const isSelf = m.user === user?.id;
                  const isInherited = !m.role; // no explicit project role = using org role
                  const score = m.evaluation_last_score;
                  const scorePct = typeof score === "number" ? Math.round(score * 100) : null;
                  const scoreColor =
                    scorePct == null
                      ? "var(--color-neutral-content-subtler)"
                      : scorePct >= 80
                        ? "var(--color-positive-content, #1f7a5a)"
                        : scorePct >= 60
                          ? "var(--color-warning-content, #b26a00)"
                          : "var(--color-negative-content, #c0353a)";
                  const tasksCell = m.tasks_limit
                    ? `${m.tasks_used ?? 0} / ${m.tasks_limit} tasks`
                    : `${m.tasks_used ?? 0} tasks`;
                  return (
                    <div
                      key={m.id}
                      className={block
                        .elem("row")
                        .mod({ paused: !!m.suspended_by_evaluation, selected: selected.has(m.id) })
                        .toClassName()}
                    >
                      <div className={block.elem("col").mod({ check: true }).toClassName()}>
                        <Checkbox
                          checked={selected.has(m.id)}
                          onChange={() => toggleSelect(m.id)}
                          ariaLabel={`Select ${m.user_email}`}
                        />
                      </div>
                      {hasCol("name") && (
                        <div
                          className={block.elem("col").mod({ member: true }).toClassName()}
                          onClick={() => m.user && history.push(`/users/${m.user}`)}
                          onKeyDown={(e) => {
                            if ((e.key === "Enter" || e.key === " ") && m.user) {
                              e.preventDefault();
                              history.push(`/users/${m.user}`);
                            }
                          }}
                          style={{ cursor: "pointer" }}
                          role="link"
                          tabIndex={0}
                          title={t("pages.settings.members_settings.open_profile")}
                        >
                          <Avatar user={{ avatar: m.user_avatar, initials: m.user_initials }} />
                          <span className={block.elem("name").toClassName()}>
                            {[m.user_first_name, m.user_last_name].filter(Boolean).join(" ") || "No name set"}
                          </span>
                          {isInherited && (
                            <span className={block.elem("badge").mod({ inherited: true }).toClassName()}>
                              Inherited
                            </span>
                          )}
                          {isSelf && <span className={block.elem("you-badge").toClassName()}>you</span>}
                          {m.suspended_by_evaluation && (
                            <span
                              className={block.elem("paused-badge").toClassName()}
                              title={t("pages.settings.members_settings.autosuspended_by_annotator_evaluation")}
                            >
                              paused
                            </span>
                          )}
                        </div>
                      )}
                      {hasCol("email") && (
                        <div className={block.elem("col").mod({ email: true }).toClassName()}>{m.user_email}</div>
                      )}
                      {hasCol("role") && (
                        <div className={block.elem("col").mod({ role: true }).toClassName()}>
                          {canManage && editingRow === m.id ? (
                            <select
                              className={block.elem("role-select").toClassName()}
                              value={m.role ?? ""}
                              // eslint-disable-next-line jsx-a11y/no-autofocus
                              autoFocus
                              onBlur={() => setEditingRow(null)}
                              onChange={async (e) => {
                                await updateRole(m, e.target.value);
                                setEditingRow(null);
                              }}
                              disabled={savingRole === m.id}
                            >
                              {ROLE_OPTIONS.map((r) => (
                                <option key={r.value} value={r.value}>
                                  {r.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className={block.elem("role-cell").toClassName()}>
                              {roleLabel(m.role)}
                              {isInherited ? (
                                <em title="This role comes from the organisation. Change it there and it changes here.">
                                  from the organisation
                                </em>
                              ) : null}
                            </span>
                          )}
                        </div>
                      )}
                      {hasCol("quality") && (
                        <div className={block.elem("col").mod({ quality: true }).toClassName()}>
                          <div className={block.elem("quality-cell").toClassName()}>
                            <span style={{ color: scoreColor, fontWeight: 600, minWidth: 44 }}>
                              {scorePct != null ? `${scorePct}%` : "—"}
                            </span>
                            <span
                              style={{
                                color: "var(--color-neutral-content-subtler)",
                                fontSize: "var(--font-size-body-small)",
                              }}
                            >
                              {m.evaluation_gt_count ? `${m.evaluation_gt_count} GT, ${tasksCell}` : tasksCell}
                            </span>
                          </div>
                        </div>
                      )}
                      {extraColumns.map((column) => (
                        <div key={column.key} className={block.elem("col").mod({ extra: true }).toClassName()}>
                          {column.render?.(m, extraData[column.key], {
                            project,
                            reload: () => setExtraRound((n) => n + 1),
                          }) ?? null}
                        </div>
                      ))}
                      {hasCol("actions") && (
                        <div className={block.elem("col").mod({ actions: true }).toClassName()}>
                          {canManage && (
                            // Icons, each saying what it does in its
                            // tooltip: four words in every row crowded
                            // out the row itself.
                            <div className={block.elem("row-actions").toClassName()}>
                              <Tooltip title="Edit">
                                <button
                                  type="button"
                                  onClick={() => (editSections.length ? setEditing(m) : setEditingRow(m.id))}
                                  aria-label={`Edit ${m.user_email ?? "this member"}`}
                                  data-testid="member-edit"
                                >
                                  <IconPencil />
                                </button>
                              </Tooltip>
                              {/* Eval auto-suspend reads differently from a
                              manual pause: the tooltip says which it is. */}
                              {m.suspended_by_evaluation ? (
                                <Tooltip title="Re-enable: paused by their score">
                                  <button
                                    type="button"
                                    onClick={() => reEnable(m)}
                                    aria-label={`Re-enable ${m.user_email ?? "this member"}`}
                                  >
                                    <IconPlayThin />
                                  </button>
                                </Tooltip>
                              ) : m.enabled === false ? (
                                <Tooltip title="Resume">
                                  <button
                                    type="button"
                                    onClick={() => resumeMember(m)}
                                    aria-label={`Resume ${m.user_email ?? "this member"}`}
                                  >
                                    <IconPlayThin />
                                  </button>
                                </Tooltip>
                              ) : (
                                <Tooltip title="Pause">
                                  <button
                                    type="button"
                                    onClick={() => pauseMember(m)}
                                    aria-label={`Pause ${m.user_email ?? "this member"}`}
                                  >
                                    <IconPauseThin />
                                  </button>
                                </Tooltip>
                              )}
                              <Tooltip title="Remove from the project">
                                <button
                                  type="button"
                                  data-tone="negative"
                                  onClick={() => removeMember(m)}
                                  aria-label={`Remove ${m.user_email ?? "this member"}`}
                                >
                                  <IconTrash />
                                </button>
                              </Tooltip>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <Pagination
            name="project-members"
            page={page}
            pageSize={pageSize}
            totalItems={total}
            urlParamName="mpage"
            /* pageSizeOptions omitted — the toolbar above owns the "per
           page" dropdown, so Pagination renders navigation only. */
            onPageLoad={(p, ps) => {
              setPage(p);
              setPageSize(ps);
            }}
            style={{ paddingTop: 16 }}
          />
        </>
      )}

      {addOpen && (
        <AddMemberModal
          projectId={projectId}
          sections={editSections}
          onClose={() => setAddOpen(false)}
          onAdded={() => {
            load();
            setExtraRound((n) => n + 1);
          }}
        />
      )}
      {editing && (
        <EditMemberModal
          projectId={projectId}
          member={editing}
          sections={editSections}
          roleOptions={ROLE_OPTIONS}
          onClose={() => setEditing(null)}
          onSaved={() => {
            load();
            setExtraRound((n) => n + 1);
          }}
        />
      )}
    </div>
  );
};

MembersSettings.menuItem = "Members";
MembersSettings.title = "Members";
// Promoted to a top-level project route at /projects/:id/members so
// it sits alongside Dashboard / Data / Settings in the project nav
// instead of being nested under Settings.
MembersSettings.path = "/members";
MembersSettings.exact = true;

// Mounts the shared project top-nav while the Members page is open.
MembersSettings.context = () => {
  const { project } = useContext(ProjectContext);
  const { user } = useAuth();
  if (!project?.id) return null;
  const role = user?.active_organization_role;
  const canManage =
    !!user &&
    (user.is_staff || user.is_superuser || ["OW", "AD", "MA"].includes(role) || project?.created_by?.id === user.id);
  return (
    <ProjectNav
      projectId={project.id}
      canManage={canManage}
      project={project}
      userRole={user?.active_organization_role}
    />
  );
};
